import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { UsersRound, Copy, Check, Package, DollarSign, Mail, Phone, Eye, EyeOff, ShieldCheck, Plus, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const milesToKm = (miles) => Math.round((miles || 0) * 1.60934);

export default function AdminCustomers() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [kpiRange, setKpiRange] = useState('all');

  const fetchCustomers = () => {
    api.get('/customers')
      .then(r => setCustomers(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchCustomers(); }, [token]);

  const inviteUrl = `${window.location.origin}/register?org=${user?.org_id}&role=client`;
  const copyLink = () => { navigator.clipboard.writeText(inviteUrl); setCopied(true); toast.success('Invite link copied!'); setTimeout(() => setCopied(false), 2000); };

  const pendingVerification = customers.filter(c => c.verification_status === 'pending' || !c.verification_status);
  const totalSpent = customers.reduce((s, c) => s + (c.total_spent || 0), 0);
  const totalLoads = customers.reduce((s, c) => s + (c.total_loads || 0), 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Customers</h1>
          <p className="text-muted-foreground text-sm">{customers.length} shippers registered</p>
        </div>
        <div className="flex items-center gap-2">
          <CreateCustomerDialog open={createOpen} onOpenChange={setCreateOpen} token={token} onCreated={fetchCustomers} />
          <Button onClick={copyLink} variant="outline" className="border-input text-muted-foreground text-xs h-9 gap-1.5">
            {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Invite Link</>}
          </Button>
        </div>
      </div>

      {/* Pending Verifications */}
      {pendingVerification.length > 0 && (
        <Card className="bg-card border-amber-500/30 mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-amber-400 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> PENDING VERIFICATION ({pendingVerification.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingVerification.map(c => (
                <div key={c.user_id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <p className="text-sm text-foreground font-medium">{c.name}</p>
                    <p className="text-xs text-muted-foreground">{c.company_name || 'No company'} · {c.email}</p>
                    {/* Verification checklist */}
                    <div className="flex gap-3 mt-1">
                      {[{ ok: !!c.company_name, label: 'Company' }, { ok: !!c.email, label: 'Email' }, { ok: !!c.phone, label: 'Phone' }].map(ch => (
                        <span key={ch.label} className={`text-[10px] flex items-center gap-0.5 ${ch.ok ? 'text-emerald-400' : 'text-red-400'}`}>
                          {ch.ok ? <Check className="w-2.5 h-2.5" /> : <AlertTriangle className="w-2.5 h-2.5" />} {ch.label}
                        </span>
                      ))}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="border-amber-500/30 text-amber-400 h-7 text-xs" onClick={() => navigate(`/admin/customers/${c.user_id}/verify`)}>
                    Review
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards with date range */}
      <div className="flex items-center gap-2 mb-3">
        {[{ id: 'all', label: 'ALL TIME' }, { id: '7d', label: '7D' }, { id: 'mtd', label: 'MTD' }].map(r => (
          <button key={r.id} onClick={() => setKpiRange(r.id)}
            className={`text-[10px] tracking-wider font-bold px-3 py-1.5 rounded-md transition-colors ${
              kpiRange === r.id ? 'bg-brand-accent/10 text-brand-accent border border-brand-accent/20' : 'text-muted-foreground border border-border hover:text-muted-foreground'
            }`}>{r.label}</button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="bg-card border-border"><CardContent className="p-4 text-center">
          <UsersRound className="w-5 h-5 text-purple-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-foreground">{customers.length}</p>
          <p className="text-[10px] text-muted-foreground">CUSTOMERS</p>
        </CardContent></Card>
        <Card className="bg-card border-border"><CardContent className="p-4 text-center">
          <Package className="w-5 h-5 text-foreground mx-auto mb-2" />
          <p className="text-2xl font-bold text-foreground">{totalLoads}</p>
          <p className="text-[10px] text-muted-foreground">TOTAL LOADS</p>
        </CardContent></Card>
        <Card className="bg-card border-border"><CardContent className="p-4 text-center">
          <DollarSign className="w-5 h-5 text-brand-accent mx-auto mb-2" />
          <p className="text-2xl font-bold text-brand-accent">${totalSpent.toLocaleString()}</p>
          <p className="text-[10px] text-muted-foreground">TOTAL REVENUE</p>
        </CardContent></Card>
      </div>

      {/* Customer Table */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">CUSTOMER</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">CONTACT</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">LOADS</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">ACTIVE</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">TOTAL KM</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">SPENT</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">JOINED</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide">STATUS</TableHead>
                <TableHead className="text-muted-foreground text-[10px] tracking-wide"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? Array(3).fill(0).map((_, i) => (
                <TableRow key={i} className="border-border"><TableCell colSpan={9}><div className="h-8 bg-muted rounded animate-pulse" /></TableCell></TableRow>
              )) : customers.map(c => (
                <TableRow key={c.user_id} className="border-border hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/admin/customers/${c.user_id}`)}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-purple-500/20 flex items-center justify-center text-xs font-bold text-purple-400">{c.name?.charAt(0)}</div>
                      <div><p className="text-xs text-foreground font-medium">{c.name}</p>{c.company_name && <p className="text-[10px] text-muted-foreground">{c.company_name}</p>}</div>
                    </div>
                  </TableCell>
                  <TableCell><div className="text-xs text-muted-foreground"><div className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</div>{c.phone && <div className="flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" />{c.phone}</div>}</div></TableCell>
                  <TableCell className="text-xs text-foreground font-bold">{c.total_loads}</TableCell>
                  <TableCell>{c.active_loads > 0 ? <Badge className="text-[10px] bg-brand-accent/20 text-foreground">{c.active_loads}</Badge> : <span className="text-xs text-muted-foreground">0</span>}</TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">{c.total_km?.toLocaleString()} km</TableCell>
                  <TableCell className="text-xs text-brand-accent font-mono">${c.total_spent?.toLocaleString()}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>
                    {c.verification_status === 'verified' ? (
                      <Badge className="text-[10px] bg-emerald-500/20 text-emerald-400">Verified ✓</Badge>
                    ) : c.verification_status === 'rejected' ? (
                      <div className="flex items-center gap-2">
                        <Badge className="text-[10px] bg-red-500/20 text-red-400">Rejected</Badge>
                        <Button size="sm" variant="outline" className="border-amber-500/30 text-amber-400 h-6 text-[10px] px-2"
                          onClick={(e) => { e.stopPropagation(); navigate(`/admin/customers/${c.user_id}/verify`); }}>
                          Review
                        </Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" className="border-amber-500/30 text-amber-400 h-6 text-[10px] px-2"
                        onClick={(e) => { e.stopPropagation(); navigate(`/admin/customers/${c.user_id}/verify`); }}>
                        Review
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    <Eye className="w-3.5 h-3.5 text-muted-foreground cursor-pointer" onClick={(e) => { e.stopPropagation(); navigate(`/admin/customers/${c.user_id}`); }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

    </div>
  );
}

function CreateCustomerDialog({ open, onOpenChange, token, onCreated }) {
  const [form, setForm] = useState({ company_name: '', email: '', phone: '', password: '' });
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleCreate = async () => {
    if (!form.email || !form.company_name) { toast.error('Company name and email required'); return; }
    if (!form.password || form.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSaving(true);
    try {
      await api.post('/users', { name: form.company_name, email: form.email, phone: form.phone, role: 'client', password: form.password });
      toast.success('Customer created! Invite email sent.');
      onOpenChange(false);
      setForm({ company_name: '', email: '', phone: '', password: '' });
      onCreated();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create customer'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-brand-accent hover:bg-yellow-500 text-brand-accent-foreground font-bold rounded-lg text-xs tracking-wide h-9 px-4">
          <Plus className="w-4 h-4 mr-1" /> CREATE CUSTOMER
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-surface border-border text-foreground max-w-sm">
        <DialogHeader><DialogTitle className="tracking-wider">Create Customer</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">Customer will receive an invite link to their email.</p>
        <div className="space-y-3 mt-2">
          <div><Label className="text-muted-foreground text-xs">Company Name</Label>
            <Input value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} className="bg-muted border-input text-foreground rounded-lg mt-1" placeholder="ACME Corp" /></div>
          <div><Label className="text-muted-foreground text-xs">Email</Label>
            <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-muted border-input text-foreground rounded-lg mt-1" placeholder="contact@company.com" /></div>
          <div><Label className="text-muted-foreground text-xs">Phone</Label>
            <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-muted border-input text-foreground rounded-lg mt-1" placeholder="416-555-0100" /></div>
          <div><Label className="text-muted-foreground text-xs">Password</Label>
            <div className="flex gap-2 mt-1">
              <div className="relative flex-1">
                <Input type={showPassword ? 'text' : 'password'} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="bg-muted border-input text-foreground rounded-lg pr-9" placeholder="Min 6 characters" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <Button type="button" variant="outline" className="border-input text-muted-foreground text-xs px-3 shrink-0" onClick={() => setForm({ ...form, password: Math.random().toString(36).slice(2, 12) })}>Generate</Button>
            </div>
          </div>
        </div>
        <Button onClick={handleCreate} disabled={saving} className="w-full bg-brand-accent hover:bg-yellow-500 text-brand-accent-foreground font-bold rounded-lg mt-4 tracking-wider">
          {saving ? 'CREATING...' : 'CREATE & SEND INVITE'}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
